import { BaseControl } from '..';
import { FormatAlignCenter, FormatAlignLeft, FormatAlignRight } from "@mui/icons-material";
import { FontFamilyControl } from '../FontFamilyControl';
import { IconButtonGroupControl } from '../IconButtonGroupControl';
import { SelectControl } from '../SelectControl';
import { NumberCssControl } from '../NumberCssControl';

/**
 * @typedef {object} FontControlOptions
 * @property {boolean} [align] - Whether to include text alignment options
 */

/**
 * @typedef {object} FontControlValue
 * @property {string} [fontFamily] - Font family
 * @property {string} [fontWeight] - Font weight (e.g. normal, bold)
 * @property {{ value: number, unit: import('../NumberCssControl').CssUnit }} [fontSize] - Font size (e.g. 16px, 1em)
 * @property {string} [fontStyle] - Font style (e.g. normal, italic)
 * @property {string} [textDecoration] - Text decoration (e.g. none, underline)
 * @property {string} [alignment] - Text alignment (e.g. left, center, right)
 */

/**
 * @extends {BaseControl<FontControlValue, FontControlOptions>}
 */
export class FontControl extends BaseControl {
    
    /**
     * @param {import('..').BaseControlDefinition<FontControlValue, FontControlOptions>} control 
     */
    constructor(control) {
        super(control);

        /** @type {Object.<string, BaseControl>} */
        this.children = {
            'fontFamily': new FontFamilyControl({
                label: 'Lettertype',
                description: 'Kies een lettertype',
                defaultValue: 'Arial',
                options: async () => {
                    return {
                        fonts: [
                            { value: 'Arial', label: 'Arial' },
                            { value: 'Times New Roman', label: 'Times New Roman' },
                            { value: 'Courier New', label: 'Courier New' },
                            { value: 'Roboto', label: 'Roboto' },
                            { value: 'Poppins', label: 'Poppins' },
                        ]
                    }
                }
            }),
            'fontSize': new NumberCssControl({
                label: 'Grootte',
                description: 'Grootte van de tekst',
                defaultValue: { value: 16, unit: 'px' },
                options: {
                    min: 6,
                    max: 200,
                    step: 1,
                }
            }),
            'fontWeight': new SelectControl({
                label: 'Dikte',
                description: '',
                defaultValue: 'normal',
                options: {
                    choices: [
                        { value: 'normal', label: 'Normaal' },
                        { value: 'bold', label: 'Vet' },
                        { value: 'bolder', label: 'Zwaarder' },
                        { value: 'lighter', label: 'Lichter' },
                    ]
                }
            }),
            'fontStyle': new SelectControl({
                label: 'Stijl',
                description: '',
                defaultValue: 'normal',
                options: {
                    choices: [
                        { value: 'normal', label: 'Normaal' },
                        { value: 'italic', label: 'Cursief' },
                        // { value: 'oblique', label: 'Schuin' },
                    ]
                }
            }),
            'textDecoration': new SelectControl({
                label: 'Decoratie',
                description: '',
                defaultValue: 'none',
                options: {
                    choices: [
                        { value: 'none', label: 'Geen' },
                        { value: 'underline', label: 'Onderstreept' },
                        // { value: 'line-through', label: 'Doorstreept' },
                    ]
                }
            }),
        };

        if ((control.options?.align ?? true) === true) {
            this.children['alignment'] = new IconButtonGroupControl({
                label: 'Tekstuitlijning',
                description: 'Uitlijning van de tekst',
                defaultValue: 'left',
                options: {
                    buttons: [
                        { 
                            value: 'left', 
                            icon: <FormatAlignLeft />, 
                            tooltip: 'Links' 
                        },
                        { 
                            value: 'center', 
                            icon: <FormatAlignCenter />, 
                            tooltip: 'Midden' 
                        },
                        { 
                            value: 'right', 
                            icon: <FormatAlignRight />, 
                            tooltip: 'Rechts' 
                        },
                    ]
                }
            })
        }
    }
}
